'use client';

import React from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Button } from './Button';
import { hasMultiplePages, hasNextPage } from './DataTable/paginationState';

interface TablePaginationProps {
  currentPage: number;
  /** Sem total conhecido, omita e informe `hasMore`: some a numeração e "Próximo" segue `hasMore`. */
  totalPages?: number;
  hasMore?: boolean;
  onPageChange: (page: number) => void;
  className?: string;
}

function pageWindow(currentPage: number, totalPages: number): number[] {
  const windowSize = Math.min(5, totalPages);
  return Array.from({ length: windowSize }, (_, i) => {
    if (totalPages <= 5) return i + 1;
    if (currentPage <= 3) return i + 1;
    if (currentPage >= totalPages - 2) return totalPages - 4 + i;
    return currentPage - 2 + i;
  });
}

export function TablePagination({
  currentPage,
  totalPages,
  hasMore = false,
  onPageChange,
  className,
}: TablePaginationProps) {
  const pagination = { page: currentPage, totalPages, hasMore };
  const safeTotalPages = totalPages !== undefined ? Math.max(1, totalPages) : null;
  const canGoNext = hasNextPage(pagination);

  if (!hasMultiplePages(pagination)) return null;

  return (
    <div
      className={`flex w-full items-center justify-between border-t border-neutral-200 px-6 py-4 ${className ?? ''}`}
    >
      <Button
        variant="neutral-tertiary"
        size="small"
        icon={<ArrowLeft className="h-4 w-4" />}
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      >
        Anterior
      </Button>

      {safeTotalPages !== null && (
        <div className="flex items-center gap-1">
          {pageWindow(currentPage, safeTotalPages).map((page) => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                currentPage === page
                  ? 'bg-brand-50 text-brand-600 font-bold'
                  : 'text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900'
              }`}
            >
              {page}
            </button>
          ))}
          {safeTotalPages > 5 && currentPage < safeTotalPages - 2 && (
            <span className="flex h-8 w-8 items-center justify-center text-neutral-400">...</span>
          )}
        </div>
      )}

      <Button
        variant="neutral-tertiary"
        size="small"
        iconRight={<ArrowRight className="h-4 w-4" />}
        onClick={() =>
          onPageChange(
            safeTotalPages !== null ? Math.min(safeTotalPages, currentPage + 1) : currentPage + 1,
          )
        }
        disabled={!canGoNext}
      >
        Próximo
      </Button>
    </div>
  );
}
